import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { dialogTitle } from './elementTrace';
import { loadUsageConfig, usageRecorder, type UsageRecorder } from './usageRecorder';

/**
 * Pomiar użycia w układzie panelu (decyzja użytkownika 22.10.2026,
 * S-22.10-02; pełny ślad działań od 23.10.2026): wejścia na ekrany i zmiany
 * filtrów, sortowania i strony listy ze zmiany trasy, kliknięcia jednym
 * delegowanym nasłuchem na dokumencie, otwarcie i zamknięcie okien
 * dialogowych (obserwator dokumentu), czas na ekranie i wysyłka przy ukryciu
 * i zamknięciu karty. Nic nie rysuje.
 *
 * Konfiguracja raz na zalogowanie (`GET /usage-events/config` — stan
 * środowiska przełącznika `usageTelemetry`, nie wybór wersji konta: pomiar
 * na środowisku akceptacyjnym widzi obie wersje). Wyłączony = nic się nie
 * zbiera i nie wysyła.
 */
export function UsageTracker({ recorder = usageRecorder }: { recorder?: UsageRecorder }) {
  const { pathname, search } = useLocation();

  useEffect(() => {
    recorder.screen(pathname);
  }, [pathname, recorder]);

  useEffect(() => {
    recorder.listLocation(pathname, search);
  }, [pathname, search, recorder]);

  useEffect(() => {
    let alive = true;
    loadUsageConfig().then((config) => {
      if (alive) recorder.configure(config);
    });
    const onClick = (e: Event) => recorder.click(e.target);
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') recorder.hidden();
      else recorder.visible();
    };
    const onPageHide = () => recorder.hidden();
    document.addEventListener('click', onClick, { capture: true, passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    const stopDialogs = watchDialogs(recorder);
    return () => {
      alive = false;
      document.removeEventListener('click', onClick, { capture: true });
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      stopDialogs();
      recorder.flush(true);
    };
  }, [recorder]);

  return null;
}

/**
 * Otwarcie i zamknięcie okien dialogowych (`role="dialog"`): tytuł okna jako
 * etykieta (`dialogTitle` — ucięty jak nagłówek), przy zamknięciu czas, przez
 * jaki było otwarte. Okna MUI rysują się w portalu — bezpośrednim dziecku
 * `body` — więc obserwujemy tylko dzieci `body` (nie całe drzewo: lista
 * zleceń i oś czasu floty zmieniają DOM bez przerwy), a przegląd idzie
 * w następnym takcie, gdy treść portalu już jest. Błąd obserwatora nigdy nie
 * przeszkadza w pracy.
 */
export function watchDialogs(recorder: UsageRecorder, root: Node = document.body): () => void {
  if (typeof MutationObserver === 'undefined' || !root) return () => undefined;
  const open = new Map<Element, { since: number; label: string | null }>();
  const scan = () => {
    try {
      const present = new Set(Array.from((root as Element).querySelectorAll?.('[role="dialog"]') ?? []));
      for (const el of Array.from(present)) {
        if (!open.has(el)) {
          const label = dialogTitle(el);
          open.set(el, { since: Date.now(), label });
          recorder.dialogOpened(label);
        }
      }
      for (const [el, info] of Array.from(open.entries())) {
        if (!present.has(el) || !el.isConnected) {
          open.delete(el);
          recorder.dialogClosed(info.label, Date.now() - info.since);
        }
      }
    } catch {
      // Pomiar nigdy nie przeszkadza w pracy.
    }
  };
  let queued: ReturnType<typeof setTimeout> | null = null;
  const later = () => {
    if (queued !== null) return;
    queued = setTimeout(() => {
      queued = null;
      scan();
    }, 0);
  };
  const observer = new MutationObserver(later);
  observer.observe(root, { childList: true });
  scan();
  return () => {
    observer.disconnect();
    if (queued !== null) clearTimeout(queued);
  };
}
